import clsx from 'clsx';
import styles from './TextContent.module.scss';

type TextContentBaseProps = {
  limitWidth?: boolean;
  center?: boolean;
  testId?: string;
  textSize?: 'sm' | 'md' | 'lg' | 'xl' | 'smMd';
  className?: string;
};

const textContentClassName = ({
  className,
  limitWidth = false,
  center = false,
  textSize = 'lg',
}: TextContentBaseProps): string =>
  clsx(styles.content, className, {
    [styles.contentLimitWidth]: limitWidth,
    [styles.contentCenter]: center,
    [styles.contentTextSizeSm]: textSize === 'sm',
    [styles.contentTextSizeMd]: textSize === 'md',
    [styles.contentTextSizeLg]: textSize === 'lg',
    [styles.contentTextSizeXl]: textSize === 'xl',
    [styles.contentTextSizeSmMd]: textSize === 'smMd',
  });

type TextContentProps = TextContentBaseProps & {
  children: React.ReactNode;
};

/**
 * Renders styled text content from React children.
 * For a raw HTML string, use `TextContentHtml`.
 */
export const TextContent: React.FC<TextContentProps> = ({ children, ...base }) => (
  <div className={textContentClassName(base)} data-testid={base.testId}>
    {children}
  </div>
);

type TextContentHtmlProps = TextContentBaseProps & {
  /** Raw HTML string — inserted via dangerouslySetInnerHTML, so it must be trusted. */
  html: string;
};

/**
 * Renders a raw HTML string with the same styling as `TextContent`.
 * The `{ __html }` wrapper is applied internally so callers cannot malform it,
 * and this component has no `children` — so the two can never conflict.
 */
export const TextContentHtml: React.FC<TextContentHtmlProps> = ({ html, ...base }) => (
  <div
    className={textContentClassName(base)}
    data-testid={base.testId}
    dangerouslySetInnerHTML={{ __html: html ?? '' }}
  />
);
